import * as PIXI from 'pixi.js';

import ProppingItem from '../ProppingItem';
import tacHelpers from '../../state/tac/tacHelpers';
import project from '../util/project';
import proppingService from '../../services/propping';
import { getProppingBounds } from '../../services/products/models';
import constants from '../../settings/constants';
import { config as asConfig } from './AdjustableConfig';
import productService, { getProduct } from '../../services/products';
import adjustableContainer from '../util/adjustableContainer';
import { ITEMS } from '../../constants';

export default class ClothesRail extends ProppingItem {
  update(args) {
    this.isExtendable = productService.isExtendable(args.item);
    super.update(args);
  }

  getSectionSprite(parent, section) {
    if (
      !section ||
      !parent ||
      parent.itemid === section.itemid ||
      !this.app.itemContainer
    ) {
      return;
    }
    return (
      this.app.itemContainer.getSprite(section.itemid) ||
      this.app.itemContainer.children.find(
        sprite => sprite.item.itemid === section.itemid
      )
    );
  }

  isMobilePortrait() {
    const { isMobile, isPortrait } = this.app.itemContainer.options;
    return isMobile && isPortrait;
  }

  getProppingItem() {
    return this.isExtendable
      ? {
          ...this.item,
          ...getProduct(asConfig.clothesrail[this.item.id].rightId),
        }
      : this.item;
  }

  getBounds() {
    const bounds = super.getBounds();
    const proppingItem = this.getProppingItem();
    const proppingHeight = getProppingBounds(proppingItem).find(
      bounds => bounds.id === '1'
    ).size.height;
    bounds.height = proppingHeight * this.ratio;

    // Compensate for padding, otherwise it will look like the shelf is included in the selection
    bounds.y += this.isMobilePortrait()
      ? constants.OUTLINE_PADDING_MOBILE_PORTRAIT
      : constants.OUTLINE_PADDING;

    return bounds;
  }

  shouldUseSmallestPropping() {
    const { app, parentItem } = this;

    // If we are either coming from the swiper or our parent (shelf) or any upright is being dragged
    return (
      !parentItem ||
      !parentItem.itemid ||
      (app.dragging &&
        (productService.isType(app.dragging.item, ITEMS.UPRIGHT) ||
          app.dragging.item.itemid === parentItem.itemid))
    );
  }

  getResourceName(movingItem, movingSprite) {
    const { item, parentItem } = this;

    const proppingItem = this.getProppingItem();

    const section = tacHelpers.getTopAncestor(this.app.tac, parentItem);

    const sectionSprite = this.getSectionSprite(parentItem, section);

    const singleProppingBounds = this.shouldUseSmallestPropping()
      ? proppingService.smallestPropping(proppingItem)
      : proppingService.pickPropping({
          isDynamic: true,
          tac: this.app.itemContainer
            ? this.app.itemContainer.tac
            : this.app.tac,
          item: proppingItem,
          parentItem,
          section: sectionSprite,
          movingItem,
          movingSprite,
        });

    let proppingName;

    if (singleProppingBounds) {
      if (!this.isExtendable) {
        proppingName = `${item.id}_propping_${singleProppingBounds.id}`;
      } else {
        proppingName = `${
          asConfig.clothesrail[this.item.id].rightId
        }_propping_${singleProppingBounds.id}`;
      }
      return {
        proppingName,
        singleProppingBounds,
      };
    }
    return {
      proppingName: `${item.id}_propping_${singleProppingBounds.id}`,
      singleProppingBounds,
    };
  }

  setSpriteVisibilities() {
    if (this.sprite) {
      this.sprite.visible = !this.isExtendable;
    }
    if (this.adjustableContainer) {
      this.adjustableContainer.visible = this.isExtendable;
    }
  }

  drawFixed(propping) {
    const { item, ratio } = this;
    const { shouldShowPropping, proppingName, singleProppingBounds } = propping;
    const { outerSize } = this.data;

    const texture = shouldShowPropping
      ? PIXI.Loader.shared.resources[proppingName].texture
      : PIXI.Loader.shared.resources[item.id].texture;

    this.sprite = this.sprite || new PIXI.Sprite();
    this.sprite.texture = texture;
    this.sprite.width = item.width * ratio;
    this.sprite.height = shouldShowPropping
      ? project.outer(singleProppingBounds.size).height * ratio
      : outerSize.height * ratio;

    if (!this.sprite.parent) {
      this.addChild(this.sprite);
    }
  }

  drawAdjustable(propping) {
    const { shouldShowPropping, proppingName, singleProppingBounds } = propping;

    this.adjustableContainer = adjustableContainer(
      this.adjustableContainer,
      this.data.outerSize,
      asConfig.clothesrail[this.item.id],
      this.ratio,
      shouldShowPropping ? { proppingName, singleProppingBounds } : {}
    );

    if (!this.adjustableContainer.parent) {
      this.addChild(this.adjustableContainer);
    }
  }

  draw(movingItem, movingSprite) {
    const { item } = this;

    const { proppingName, singleProppingBounds } = this.getResourceName(
      movingItem,
      movingSprite
    );

    item.propping = singleProppingBounds.id;
    const shouldShowPropping =
      this.shouldShowPropping([singleProppingBounds]) &&
      !this.options.measurementsActive;

    const proppingInfo = {
      shouldShowPropping,
      proppingName,
      singleProppingBounds,
    };

    if (this.isExtendable) {
      this.drawAdjustable(proppingInfo);
    } else {
      this.drawFixed(proppingInfo);
    }

    this.setSpriteVisibilities();
  }
}
